import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { getHasElementsInArray } from 'utils';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { getIsRfiStatusClosed } from 'router/subrouters/DataRoom/utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import RfiAddRecipientsModal from './components/RfiAddRecipientsModal';
import RfiRecipientListItem from './components/RfiRecipientListItem';
import { EMPTY_ARRAY } from 'data/global';

import './RfiRecipients.styles.scss';

type RfiRecipientsProps = {
  className?: string;
  rfi: DataRoomRFI;
  onRfiUpdate: (rfi: DataRoomRFI) => void;
};

const RfiRecipients: React.FC<RfiRecipientsProps> = ({
  className,
  rfi,
  onRfiUpdate,
}) => {
  const classes = classNames('rfi-recipients', className);

  const [addRecipientsModal, addRecipientsModalRef] =
    useCallbackRef<ModalRef>();

  const {
    _id,
    status,
    recipients = EMPTY_ARRAY,
    allRecipientsWithReminders = EMPTY_ARRAY,
  } = rfi || {};

  const isClosed = getIsRfiStatusClosed(status);

  const { t } = useTranslation();
  const { showActionUndo } = useContext(RfiContext);

  const onUndoReminderSent = useCallback(
    async (email: string) => {
      await dataRoomApi.rfi.undoSendReminders(_id, email);

      onRfiUpdate({ ...rfi, allRecipientsWithReminders });
    },
    [_id, allRecipientsWithReminders, rfi, onRfiUpdate],
  );

  const onReminderSent = useCallback(
    (email: string) => {
      onRfiUpdate({
        ...rfi,
        allRecipientsWithReminders: allRecipientsWithReminders.map(
          (recipient) =>
            recipient.email !== email
              ? recipient
              : {
                  ...recipient,
                  reminders: [...recipient.reminders, new Date()],
                },
        ),
      });

      showActionUndo(t('sendRemindersSuccess', { count: 1 }), () =>
        onUndoReminderSent(email),
      );
    },
    [
      allRecipientsWithReminders,
      rfi,
      onRfiUpdate,
      onUndoReminderSent,
      showActionUndo,
      t,
    ],
  );

  return (
    <div className={classes}>
      <div className="rfi-recipients__header">
        <p className="rfi-recipients__label">{t('recipients')}</p>

        {!isClosed && (
          <>
            <Button
              variant="outline"
              onClick={() => addRecipientsModal?.open()}
            >
              {t('addRecipients')}
            </Button>
            <RfiAddRecipientsModal
              ref={addRecipientsModalRef}
              dataRoomRfiId={_id}
              existingRecipients={recipients}
              onRfiUpdate={onRfiUpdate}
            />
          </>
        )}
      </div>

      {getHasElementsInArray(allRecipientsWithReminders) && (
        <ul>
          {allRecipientsWithReminders.map(({ email, reminders }) => (
            <RfiRecipientListItem
              key={email}
              dataRoomRfiId={_id}
              email={email}
              reminders={reminders}
              isClosed={isClosed}
              onReminderSent={onReminderSent}
            />
          ))}
        </ul>
      )}

      <p className="rfi-recipients__description">
        {t('recipientsRemindersDescription')}
      </p>
    </div>
  );
};

export default RfiRecipients;
